import { isEqual } from 'underscore';
import type { GraphicInterface, OverlayUploadData } from '../stores/types/graphics';
import {
	animationOptions,
	directionals,
	durationOptions,
} from '../components/right-panel/settings/animations/types';
import { GraphicDimensions, MediaDimensions } from '../stores/types/types';
import { UploadCloudConvert } from '../connection/media';
import type { Meeting } from '../stores/types/session';
import {
	IMG_TYPES,
	MAX_FILE_SIZE,
	MEDIA_UPLOAD_ERROR_TYPES,
	NON_DOC_FILE_TYPES,
	UPLOAD_STATUS,
	UPLOAD_TYPES,
} from '../types/constants';
import { CloudConvertUploadData } from '../types/interfaces';
import {
	buildMediaUrlPrefix,
	checkFileName,
	checkIfFileNeedsCloudConvert,
	checkWebMDuration,
	createMediaFile,
	getTags,
	createThumbnailOrPoster,
	getFileExtensionGivenFile,
	getMediaDimensionsGivenUrl,
	mediaUploadError,
	resizeImage,
	uploadImageVideoToS3,
} from './media';
import store from '../stores/main';
import * as GraphicsActions from '../stores/actions/graphics';
import { random } from './random';
import { isVideo } from './utils';
import type { Shot } from '../stores/types/shots';
import type { RelativePosition } from '../stores/types/user-interface';

interface GraphicDimensionOptions {
	longEdgeScale?: number;
	canvasX?: number;
	canvasY?: number;
}

function getOverlayPosCol(x: number) {
	if (x > 75) {
		return 4;
	}
	if (x > 50) {
		return 3;
	}
	if (x > 25) {
		return 2;
	}
	return 1;
}

export function getAnimationDirection(value: number): string {
	switch (value) {
		case 1:
			return 'right';
		case 2:
			return 'up';
		case 3:
			return 'down';
		default:
			return 'left';
	}
}

export function getAnimationName(graphic: GraphicInterface, isOutAnimation = false): string {
	const { animation } = graphic;
	const animationIndex = !isOutAnimation
		? animation?.in_animation
		: animation?.out_animation;
	const directionValue = !isOutAnimation
		? animation?.in_direction
		: animation?.out_direction;
	const durationValue = !isOutAnimation
		? animation?.in_duration
		: animation?.out_duration;

	const posColX = getOverlayPosCol(graphic?.pos_x ?? 0);
	const posColY = getOverlayPosCol(graphic?.pos_y ?? 0);

	const animationType = animationOptions[animationIndex ?? 0]?.value;
	const direction = getAnimationDirection(directionValue ?? 0);
	const duration = durationOptions[durationValue ?? 2].value * 1000;
	const outIn = !isOutAnimation
		? ''
		: '-out';

	if (animationType !== 'none') {
		const posColToUse = ['up', 'down'].includes(direction)
			? posColY
			: posColX;
		return directionals?.includes(animationType)
			? `${animationType}-${direction}${outIn}-${posColToUse}-${duration}`
			: `${animationType}${outIn}-${duration}`;
	}
	return '';
}

export function compareGraphic(graphics: GraphicInterface[], updatedGraphic: GraphicInterface) {
	const oldGraphic = graphics.find((graphic: GraphicInterface) => graphic.id === updatedGraphic.id);
	let isUpdated = false;

	if (oldGraphic) {
		const oldGraphicPosition = {
			pos_x: oldGraphic.pos_x,
			pos_y: oldGraphic.pos_y,
			rotate: oldGraphic.rotate,
			scale_x: oldGraphic.scale_x,
			scale_y: oldGraphic.scale_y,
			animation: JSON.stringify(oldGraphic.animation),
			is_global: oldGraphic.is_global,
			play_audio: oldGraphic.play_audio,
			loops: oldGraphic.loops,
		};

		const updatedGraphicPosition = {
			pos_x: updatedGraphic.pos_x,
			pos_y: updatedGraphic.pos_y,
			rotate: updatedGraphic.rotate,
			scale_x: updatedGraphic.scale_x,
			scale_y: updatedGraphic.scale_y,
			animation: JSON.stringify(updatedGraphic.animation),
			is_global: updatedGraphic.is_global,
			play_audio: updatedGraphic.play_audio,
			loops: updatedGraphic.loops,
		};

		isUpdated = !isEqual(oldGraphicPosition, updatedGraphicPosition);

		return isUpdated
			? { ...oldGraphic, ...updatedGraphic }
			: null;
	}

	return null;
}

export function getDimensionsOfGraphic(
	dimensionsInPx: MediaDimensions,
	horizontalScale: number = 60,
	verticalScale: number = 75,
	options: GraphicDimensionOptions = {},
	capFull: boolean = true,
): GraphicDimensions {
	/*
	We want to generate the graphic with an x and a y that are (ratio wise) as
	close to the original image's x:y ratio as we can get. We use 1080/1920 as
	a base to correlate to our canvas' ratio, then we change the y value to match the
	image's original ratio. This makes our green selection box nice and flush with the image.
	*/

	const {
		height = 1,
		width = 1,
	} = dimensionsInPx;
	const maxHorizontalScale = (capFull && horizontalScale < 100)
		? horizontalScale
		: 100;
	const maxVerticalScale = (capFull && verticalScale < 100)
		? verticalScale
		: 100;
	const {
		longEdgeScale = height > width ? maxVerticalScale : maxHorizontalScale,
		canvasX = 1920,
		canvasY = 1080,
	} = options;
	const shortEdgeScale = height > width
		? (longEdgeScale * canvasY) / canvasX
		: (longEdgeScale * canvasX) / canvasY;

	let xScale;
	let yScale;

	if (height > width) {
		// if portrait image, have height be around 75% of canvas height
		yScale = longEdgeScale;
		xScale = shortEdgeScale;
		xScale *= (width / height);
	} else {
		// if landscape image, width is around 60% canvas width
		xScale = longEdgeScale;
		yScale = shortEdgeScale;
		yScale *= (height / width);
	}

	// Long edge should not exceed 100%
	if (capFull && (xScale > 100 || yScale > 100)) {
		const max = Math.max(xScale, yScale);

		xScale = (xScale / max) * 100;
		yScale = (yScale / max) * 100;
	}

	// returns the % width/height the graphic should be
	return {
		xScale,
		yScale,
	};
}

export async function buildOverlay(
	originalName: string,
	images: string,
	grUserId: number,
	channel: number,
	meetingId: number,
	uploadId: string | null,
	cloudConvertUploadData: CloudConvertUploadData | undefined,
	template: string | null,
	provider: string | null,
	relativePosition: RelativePosition | null,
	customScale: GraphicDimensions | null,
): Promise<GraphicInterface> {
	// we cannot get media dimensions for some items until they have been converted.
	// set the initial state to -1 as a way to trigger setting media dimensions
	// as an overlay can be resized to 0, but not less than 0
	let dimensionsAdjusted: GraphicDimensions = { xScale: -1, yScale: -1 };
	if (images) {
		const dimensionsInPx = await getMediaDimensionsGivenUrl(images.split(',')[0]);
		dimensionsAdjusted = getDimensionsOfGraphic(
			dimensionsInPx,
			customScale?.xScale,
			customScale?.yScale,
		);
	}

	let posX = 5;
	let posY = 10;
	if (relativePosition) {
		posX = Math.round(relativePosition.x - (dimensionsAdjusted.xScale / 2));
		posY = Math.round(relativePosition.y - (dimensionsAdjusted.yScale / 2));
	}

	const graphic: GraphicInterface = {
		animation: { name: 'none', id: 0 },
		animation_css: '',
		scale_x: dimensionsAdjusted.xScale,
		scale_y: dimensionsAdjusted.yScale,
		status: true,
		animation_direction: 'none',
		animation_duration: 0,
		canvas_scalar: 0,
		channel,
		created_by: grUserId,
		edited_by: grUserId,
		images: images ? images.split(',')[0] : '',
		meeting: meetingId,
		original_name: originalName,
		pos_x: posX,
		pos_y: posY,
		rotate: 0,
		title: originalName,
		z_index: 0,
		loops: false,
		play_audio: true,
		is_global: false,
		upload_id: uploadId,
		shots: [],
		cloud_convert_id: cloudConvertUploadData ? cloudConvertUploadData.id : '',
		convert_status: '',
		thumbnail_url: '',
		provider,
		template_type: template,
	};

	return graphic;
}

export interface OverlayUploadInitFunc { (uploadData: OverlayUploadData, form?: any): void; }
export interface OverlayUploadSuccessFunc {
	(uploadData: OverlayUploadData, imgUrl: string): Promise<void>;
}
export interface OverlayUploadErrFunc { (uploadData?: OverlayUploadData): void; }
export interface OverlayUploadBeforeInitFunc { (uploadData: OverlayUploadData): void; }
export async function saveTitleAsOverlay(
	name: string,
	imageUrl: string,
	dispatch: Function,
	user: any,
	meeting: Meeting,
	postSaveCallback: Function,
	token: string | null,
	enabledGraphics: number[] | null,
	template: string | null = null,
	provider: string | null = null,
	relativePosition: RelativePosition = { x: 5, y: 10 },
): Promise<void> {
	if (meeting && user) {
		const graphic = await buildOverlay(
			name,
			imageUrl,
			user.gr_user,
			meeting.channel,
			meeting.meeting,
			null,
			undefined,
			template,
			provider,
			relativePosition,
			{ xScale: 40, yScale: 50 },
		);

		dispatch(GraphicsActions.addGraphicsAction(
			token ?? '',
			graphic,
			(graphic1: GraphicInterface) => {
				const enableGraphicId = graphic1?.id;
				if (enabledGraphics && enableGraphicId) {
					postSaveCallback(graphic1);
					dispatch(GraphicsActions.setEnabledGraphics(
						Array.from(new Set([...enabledGraphics, enableGraphicId])),
					));
				}
			},
		));
	}
}

//  This seems to be the main area where the upload is happening
export const doUploadOverlay = async (
	meeting: Meeting,
	token: string,
	files: FileList,
	onUploadInit: Function,
	onUploadSuccess: OverlayUploadSuccessFunc,
) => {
	if (!meeting || !token) return;
	const uploadData: OverlayUploadData = {
		uploadId: random(10),
		originalName: files[0].name,
		totalBytes: files[0].size,
	};

	if (files[0].size > MAX_FILE_SIZE) {
		mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_SIZE, `The maximum file size for uploads is 5GB. The file ${files[0].name} is ${(files[0].size / 1024 / 1024 / 1024).toFixed(1)}GB`);
		return;
	}

	// the directory this file will live at in aws...i think
	const prefix = buildMediaUrlPrefix(meeting.channel, meeting.meeting_id);
	const fileExtension = getFileExtensionGivenFile(files[0]);

	if (!fileExtension || !NON_DOC_FILE_TYPES.some((type: string) => type === fileExtension)) {
		mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.CREATE_OVERLAY);
		return;
	}

	// prepends a UUID to each file's file.name
	const list = new DataTransfer();
	Array.from(files).forEach((file) => list.items.add(createMediaFile(file)));
	const filesWithRandomKey: FileList = list.files;

	let cloudConvertVideoFlags = null;
	if (fileExtension === 'mov') {
		cloudConvertVideoFlags = { convertVideoBitrate: true, convertToWebM: true };
	} else if (fileExtension === 'mp4') {
		cloudConvertVideoFlags = { convertVideoBitrate: true, convertToWebM: false };
	} else if (fileExtension === 'webm') {
		cloudConvertVideoFlags = await checkWebMDuration(filesWithRandomKey[0]);
		if (!cloudConvertVideoFlags) {
			mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
			return;
		}
	}

	// Ran into a thumbnail issue just with converted mp4s.
	// For just s3 uploads (jpg, png, etc...) we strip all the special chars out above ^.
	// For just Cloud Convert we can pass it anything,
	// but we can't modify the filename before sending the FileList.
	// Converted mp4s use CC and also create the thumbnail below and upload directly to S3.
	// Because of the way thumbnails are loaded based off of the "images" column in gr_media,
	// we can't strip the special chars off of the filename in just this case because Cloud Convert
	// will return a different filepath for "images".
	// So unless we refactor the way we load the thumbnails images (maybe add a thumbnails column)
	// we just don't allow special chars for converted mp4s for now
	if (!checkFileName(files[0].name)) {
		return;
	}

	const needsCloudConvert = checkIfFileNeedsCloudConvert(cloudConvertVideoFlags, fileExtension);
	if (needsCloudConvert) {
		const ext = cloudConvertVideoFlags?.convertToWebM ? 'mov_webm' : getFileExtensionGivenFile(filesWithRandomKey[0]);
		const tags = getTags('t:overlay');
		const result = await UploadCloudConvert(
			token,
		meeting.channel,
			filesWithRandomKey,
			ext,
			prefix,
			tags,
			(request) => {
				onUploadInit(
					uploadData.originalName,
					UPLOAD_TYPES.OVERLAY,
					uploadData.uploadId,
					request,
				);
			},
		);
		if (result.status === UPLOAD_STATUS.ERROR) {
			mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
		} else if (result.status === UPLOAD_STATUS.LOAD && result.data) {
			uploadData.cloudConvertUploadData = result.data;
			onUploadSuccess(uploadData, '');
		}
	} else {
		let fileToUpload = filesWithRandomKey[0];
		if (IMG_TYPES.includes(fileExtension)) {
			// resize an image if it exceeds 4K width (3840 pixels) or height (2160 pixels)
			fileToUpload = await resizeImage(filesWithRandomKey[0], fileExtension, 3840, 2160);
		}
		// upload to aws instead of cloud convert
		const imageUrl = await uploadImageVideoToS3(
			fileToUpload,
			prefix,
			meeting.channel,
			(request) => {
				onUploadInit(
					uploadData.originalName,
					UPLOAD_TYPES.OVERLAY,
					uploadData.uploadId,
					request,
				);
			},
		);
		if (imageUrl) {
			// Create thumbnail
			await createThumbnailOrPoster(filesWithRandomKey[0], prefix, false, meeting.channel);
			// Create a poster image if graphic is a video
			if (isVideo(filesWithRandomKey[0].name)) {
				await createThumbnailOrPoster(filesWithRandomKey[0], prefix, true, meeting.channel);
			}

			onUploadSuccess(uploadData, imageUrl);
		}
	}
};

export const isAutoInGraphic = (graphic?: GraphicInterface) => graphic?.animation.build_in !== 1;

// Enhancement idea: allow for a generic array type
export const updateEnabledOverlays = (
	enabledOverlays: number[],
	addEnabledOverlays: number[],
	removeEnabledOverlays: number[],
): number[] => Array.from(
	new Set([
		...enabledOverlays.filter(
			(graphicId) => !removeEnabledOverlays?.includes(graphicId),
		),
		...addEnabledOverlays,
	]),
);

export const updateEnabledOverlaysState = (
	updatedOverlays: GraphicInterface[],
	currentOverlays: GraphicInterface[],
	enabledOverlays: number[] | null = [],
) => {
	const enabledGraphicsState = enabledOverlays
		? [...enabledOverlays]
		: [];

	let overlaysToEnable: number[] = [];
	let overlaysToDisable: number[] = [];

	updatedOverlays.forEach((updatedGraphic) => {
		const { id } = updatedGraphic;
		const isAuto = isAutoInGraphic(updatedGraphic);
		const existingOverlay = currentOverlays.find((graphic) => graphic.id === id);
		const existingIsAuto = isAutoInGraphic(existingOverlay);

		if (id && isAuto !== existingIsAuto) {
			if (isAuto) {
				overlaysToEnable = [...overlaysToEnable, id];
			} else {
				overlaysToDisable = [...overlaysToDisable, id];
			}
		}
	});

	if (overlaysToEnable.length || overlaysToDisable.length) {
		store.dispatch(
			GraphicsActions.setEnabledGraphics(
				updateEnabledOverlays(
					enabledGraphicsState,
					overlaysToEnable,
					overlaysToDisable,
				),
			),
		);
	}
};

export async function getInitialGraphicDimensions(
	graphic: GraphicInterface,
	allGraphics: GraphicInterface[],
	token: string | null,
) {
	if (!token) return;
	const dimensionsInPx = await getMediaDimensionsGivenUrl(graphic.images.split(',')[0]);
	const dimensionsAdjusted = getDimensionsOfGraphic(dimensionsInPx);
	const newGraphic = {
		...graphic,
		scale_x: dimensionsAdjusted.xScale,
		scale_y: dimensionsAdjusted.yScale,
	};
	const updatedGraphic = compareGraphic(allGraphics, newGraphic);
	if (updatedGraphic) {
		store.dispatch(GraphicsActions.editGraphicsAction(
			token,
			updatedGraphic,
			() => { },
		));
	}
}

// when changing shots, are we moving forward?
export function isMovingForward(lastShotId: number, currentShotId: number, shots: Shot[]):boolean {
	// eslint-disable-next-line camelcase
	const shotIdArray: number[] = shots.map(({ shot_id }) => shot_id);
	const firstPosition = shotIdArray.indexOf(lastShotId);
	const secondPosition = shotIdArray.indexOf(currentShotId);

	if (firstPosition < secondPosition) {
		return true;
	}

	return false;
}

// Is this same graphic in this current shot also present in the previous shot?
export function isGraphicInPreviousShot(
	currentShotId: Number,
	shots: Shot[],
	graphicId: number,
): boolean {
	const shotIndex: any = shots.findIndex((sh) => sh.shot_id === currentShotId);
	if (shotIndex === 0) return false; // first item, cant have previous
	return !!shots[shotIndex - 1].shot_data.shotGraphics?.includes(graphicId);
}

// Is this same graphic in this current shot also present in the next shot?
export function isGraphicInNextShot(
	currentShotId: Number,
	shots: Shot[],
	graphicId: number,
): boolean {
	const shotIndex: any = shots.findIndex((sh) => sh.shot_id === currentShotId);
	if (shotIndex >= shots.length - 1) return false; // last element, cant have next
	return !!shots[shotIndex + 1].shot_data.shotGraphics?.includes(graphicId);
}

// if isGraphicInPreviousShot AND next shot does not contain me then I
// am a graphic in the last shot of a series of shots that contain me.
export function isLastShotWithBackToBackGraphic(
	currentShotId: Number,
	shots: Shot[],
	graphicId: number,
): boolean {
	if (
		isGraphicInPreviousShot(currentShotId, shots, graphicId)
		&& !isGraphicInNextShot(currentShotId, shots, graphicId)
	) {
		return true;
	}

	return false;
}

// if !isGraphicInPreviousShot AND graphic is in next shot then I am a
// graphic in the first shot of a series of shots that contain me.
export function isFirstShotWithBackToBackGraphic(
	currentShotId: number,
	shots: Shot[],
	graphicId: number,
): boolean {
	if (
		isGraphicInNextShot(currentShotId, shots, graphicId)
		&& !isGraphicInPreviousShot(currentShotId, shots, graphicId)
	) {
		return true;
	}

	return false;
}

// Behavior requirements for overlays:
// 1. Shots should always FIRST default to an overlays animation settings
// 2. Special Case (EXCEPTION): when shots side by side share an overlay,
//		the logic carries forwards and backwards between the two shots.
//		If navigate away from side by side shots with shared overlays
//		and then navigate back to ANY of the shots in the sequence, the
//		shot should behave as if it’s the first time playing.
// Logic to achieve above behavior:
// 1. If build_in === 1 (manual) do not add to enabledOverlays else do.
// 2. Special Case (EXCEPTION):
//		if moving forward, any overlay that has a previous shot with same
//			overlay should keep visibility status of previous shot
//		if moving backwords, any overlay that has a next shot with the same
//			overlay should keep visibility status of the next shot

export function shouldPushToEnabled(
	enabledOverlays: number[] | null,
	specialCase: boolean,
	graphic: any,
) : boolean {
	if (specialCase) {
		if (enabledOverlays?.includes(graphic.id)) {
			return true;
		}
	}
	if (graphic.id && graphic.animation?.build_in !== 1) {
		return true; // graphicsIds.push(gr.id)
	}
	return false;
}

export function getEnabledOverlays(
	shots: Shot[],
	currentShot: Shot,
	graphics: GraphicInterface[],
	enabledOverlays: number[],
	movingForward: boolean = true,
	animatedGraphics: number[] = [],
): number[] {
	// array of graphic ids that should be visible for this shot.
	const graphicsIds: number[] = [];
	const shotGraphics = currentShot?.shot_data?.shotGraphics;

	const currShotGraphics = graphics.filter(
		(gr) => gr.id && shotGraphics && shotGraphics.includes(gr.id),
	);

	if (currShotGraphics.length === 0) return graphicsIds;

	// check each graphic in this shot to see what its visibility should be set to
	// if should be visible, add to graphicsIds array.  If not, then don't
	// eslint-disable-next-line array-callback-return
	currShotGraphics?.map((gr) => {
		let specialCase;
		let shouldPush = false;

		if (gr.id && !animatedGraphics?.includes(gr.id)) {
			const isManual: boolean = gr.animation?.build_in === 1;

			shouldPush = !isManual;

			if (movingForward) {
				if (!isFirstShotWithBackToBackGraphic(currentShot.shot_id, shots, gr.id)) {
					specialCase = isGraphicInPreviousShot(currentShot.shot_id, shots, gr.id);
					shouldPush = shouldPushToEnabled(enabledOverlays, specialCase, gr);
				}
			} else if (!isLastShotWithBackToBackGraphic(currentShot.shot_id, shots, gr.id)) {
				specialCase = isGraphicInNextShot(currentShot.shot_id, shots, gr.id);
				shouldPush = shouldPushToEnabled(enabledOverlays, specialCase, gr);
			}
		}

		if (shouldPush && gr.id) {
			graphicsIds.push(gr.id);
		}
	});

	return graphicsIds;
}
